/*
 * Форматы фото-страниц Comabooks для админки (зеркало backend/src/data/comabooksPhotos.ts).
 * photo.status в админке = photos.position: `cb:<status>` (или сырой legacy-статус).
 * Превью повторяет печать (backend services/pdfGeneratorComabooks.ts drawPhotoPage).
 */
import React from "react";
import pdfVertical from "../../assets/pdfvertical.png";
import pdfSquare from "../../assets/pdfsquare.png";
import pdfHorizontal from "../../assets/pdfhorizontal.png";

/** Дизайн книги — Comabooks. false вернёт пикеры Lovestoria (рамки/слоты/цвет). */
export const COMABOOKS_BOOK_DESIGN = true;

export type CbPhotoStatus = "top" | "center" | "bottom" | "vertical" | "square" | "horizontal";

export const CB_PHOTO_STATUSES: CbPhotoStatus[] = ["center", "top", "bottom", "vertical", "square", "horizontal"];

export const CB_PHOTO_LABELS: Record<CbPhotoStatus, string> = {
  center: "весь лист · по центру",
  top: "весь лист · сверху",
  bottom: "весь лист · снизу",
  vertical: "рамка · вертикально",
  square: "рамка · квадрат",
  horizontal: "рамка · горизонтально",
};

const isStatus = (v: string): v is CbPhotoStatus => (CB_PHOTO_STATUSES as string[]).includes(v);

export const encodeCbPhoto = (s: CbPhotoStatus) => `cb:${s}`;

export function decodeCbPhoto(position: string | null | undefined): CbPhotoStatus {
  const raw = (position ?? "").trim();
  if (raw.startsWith("cb:")) {
    const s = raw.slice(3);
    return isStatus(s) ? s : "center";
  }
  if (isStatus(raw)) return raw;
  const [preset, , desc] = raw.split("::");
  if (preset === "portrait" || preset === "portrait-tall") return "vertical";
  if (preset === "square") return "square";
  if (preset === "landscape" || preset === "landscape-wide") return "horizontal";
  if (preset === "fullbleed") {
    const pos = (desc ?? "").split(":")[0];
    return pos === "top" || pos === "bottom" ? pos : "center";
  }
  return "center";
}

const framed = (s: CbPhotoStatus) => s === "vertical" || s === "square" || s === "horizontal";

// Геометрия печати: макет 1090.68 × 1625.68, лист 126×186 мм (срез ≈7.8 сверху/снизу).
const CH = 11.34;
const LW = 1068 + CH * 2;
const LH = 1603 + CH * 2;
const CROP = (LH - (LW * 186) / 126) / 2;
const u = (n: number) => `${((n * 100) / LW).toFixed(4)}cqw`;
const FRAME_SRC = { vertical: pdfVertical, square: pdfSquare, horizontal: pdfHorizontal };
const FRAME_RATIO = { horizontal: 0.5263, square: 0.8896, vertical: 1.4627 };

export const CbPhotoPreview: React.FC<{
  status: string | null | undefined;
  photoUrl: string;
  date: string;
  description: string;
  hideDate?: boolean;
  hideDescription?: boolean;
}> = ({ status, photoUrl, date, description, hideDate, hideDescription }) => {
  const s = decodeCbPhoto(status);
  const isFramed = framed(s);
  const showDate = !hideDate && !!date;
  const showDesc = !hideDescription && !!description;
  const y = s === "top" ? 200 : s === "bottom" ? 1100 : s === "center" ? 851 : 130;
  const dateY = (isFramed ? 130 : y) + CH;
  const descY = (isFramed ? (showDate ? 170 : 130) : showDate ? y + 80 : y) + CH;
  const fs = 42 * 0.75;
  const color = isFramed ? "#000" : "#fff";
  const text = (top: number, extra: React.CSSProperties, children: string) => (
    <div
      style={{
        position: "absolute",
        left: u(534 + CH),
        top: u(top - CROP - fs * 0.99),
        transform: "translateX(-50%)",
        width: u(668),
        textAlign: "center",
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: u(fs),
        lineHeight: 1.15,
        color,
        ...extra,
      }}
    >
      {children}
    </div>
  );
  let box: React.CSSProperties | undefined;
  if (isFramed) {
    const fh = 762 * FRAME_RATIO[s as keyof typeof FRAME_RATIO];
    box = { position: "absolute", left: u((LW - 762) / 2), top: u((LH - fh) / 2 - CROP), width: u(762), height: u(fh), objectFit: "cover" };
  }
  return (
    <div style={{ containerType: "inline-size", width: "100%" } as React.CSSProperties}>
      <div style={{ position: "relative", width: "100%", aspectRatio: "126 / 186", background: "#fff", overflow: "hidden" }}>
        {!isFramed && photoUrl && (
          <img src={photoUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        )}
        {!isFramed && (!hideDate || !hideDescription) && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.2)" }} />
        )}
        {isFramed && photoUrl && <img src={photoUrl} alt="" style={box} />}
        {isFramed && (
          <img
            src={FRAME_SRC[s as keyof typeof FRAME_SRC]}
            alt=""
            style={{ position: "absolute", left: 0, top: u(-CROP), width: "100%", height: u(LH) }}
          />
        )}
        {showDate && text(dateY, { whiteSpace: "nowrap" }, date)}
        {showDesc && text(descY, isFramed ? { fontWeight: 700 } : { fontStyle: "italic" }, description)}
      </div>
    </div>
  );
};

/** Пикер формата (6 кнопок) — заменяет «Рамку»/слоты/цвет Lovestoria. */
export const CbPhotoFormatPicker: React.FC<{ status: string | null | undefined; onPick: (encoded: string) => void }> = ({
  status,
  onPick,
}) => {
  const cur = decodeCbPhoto(status);
  return (
    <div className="ep-photo-field">
      <span>Формат</span>
      <div className="ep-photo-positions">
        {CB_PHOTO_STATUSES.map((s) => (
          <button key={s} className={`ep-photo-pos-btn ${cur === s ? "active" : ""}`} onClick={() => onPick(encodeCbPhoto(s))}>
            {CB_PHOTO_LABELS[s]}
          </button>
        ))}
      </div>
    </div>
  );
};
